import { providerApi } from '@/api/ProviderApi'
import type { UpcomingVisit } from '@/api/types/ProviderApi'
import { checkInPollMs } from '@/features/visits/checkIn'
import { useQuery } from '@tanstack/react-query'
import { format } from 'date-fns'

/**
 * Prefixed with the old single-visit key so every invalidation after a booking, reschedule,
 * confirmation or cancellation refreshes this list without being told about it.
 */
export const UPCOMING_VISITS_QUERY_KEY = ['upcoming-appointment', 'list']

const formatStartTime = (visit: UpcomingVisit): UpcomingVisit => ({
	...visit,
	startTimeFormatted: `${format(visit.startTimeLocal, 'MMM do')} at ${format(
		visit.startTimeLocal,
		'h:mm a'
	)}`,
})

const byStartTime = (a: UpcomingVisit, b: UpcomingVisit) =>
	a.startTimeUtc.localeCompare(b.startTimeUtc)

/** The patient's upcoming visits, soonest first. */
const useFetchUpcomingVisits = () => {
	const { data, isLoading, isError, refetch } = useQuery({
		queryKey: UPCOMING_VISITS_QUERY_KEY,
		queryFn: () => providerApi.fetchUpcomingVisits(),
		select: (visits) => [...visits].sort(byStartTime).map(formatStartTime),
		refetchOnWindowFocus: true,
		/*
		 * Polling, but only when a door is about to open.
		 *
		 * Check-in flips from shut to open on a clock the app does not own, and a member standing
		 * in a lobby should not have to pull to refresh to find out. The web page solves this by
		 * polling every thirty seconds for its whole life — which it can afford, because it only
		 * exists inside the window it is watching.
		 *
		 * This list does not. It is on screen three weeks before a visit and every day after
		 * booking one, so the same interval would be a request every thirty seconds, for a month,
		 * to learn nothing. So the soonest visit decides: within an hour of its check-in opening,
		 * poll; otherwise do not, and let focus and invalidation do the work they already do.
		 */
		refetchInterval: (query) => checkInPollMs(query.state.data?.[0]?.checkIn, new Date()),
	})

	return {
		visits: data,
		isLoading,
		isError,
		refetch,
	}
}

export { useFetchUpcomingVisits }
